/**
 * PostHog analytics service — wrapper around posthog-js.
 * All methods are no-ops when:
 *   - VITE_POSTHOG_ENABLED !== 'true', OR
 *   - the user has not given analytics consent
 *
 * posthog-js is loaded dynamically ONLY after consent (RGPD-strictest approach).
 */
import type { PostHogInterface } from 'posthog-js'

interface PostHogUser {
  id: string
  email?: string
  role?: string
  orgSlug?: string
}

const ENABLED = import.meta.env.VITE_POSTHOG_ENABLED === 'true'
const API_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://eu.i.posthog.com'

let _posthog: PostHogInterface | null = null
let _initialized = false

export const posthogService = {
  /** Called from main.ts when consent is granted. Dynamically loads posthog-js. */
  async init(): Promise<void> {
    if (!ENABLED || !API_KEY || _initialized) return
    const { default: posthog } = await import('posthog-js')
    posthog.init(API_KEY, {
      api_host: HOST,
      person_profiles: 'identified_only',
      capture_pageview: false,           // We handle $pageview manually via router
      capture_pageleave: true,
      session_recording: {
        maskAllInputs: true,             // Mask sensitive inputs by default
        maskTextSelector: '[data-ph-mask]', // Opt-in additional masking
      },
      persistence: 'localStorage',
      loaded(ph) {
        _posthog = ph
        _initialized = true
      },
    })
  },

  /** Identify an authenticated user. */
  identify(user: PostHogUser): void {
    if (!_posthog) return
    _posthog.identify(user.id, {
      email: user.email,
      role: user.role,
      org_slug: user.orgSlug,
    })
  },

  /** Capture a custom event. */
  capture(event: string, properties?: Record<string, unknown>): void {
    if (!_posthog) return
    _posthog.capture(event, properties)
  },

  /** Track a page view (called from router afterEach). */
  trackPageView(path: string): void {
    if (!_posthog) return
    _posthog.capture('$pageview', { $current_url: path })
  },

  /** Reset identity on logout. */
  reset(): void {
    if (!_posthog) return
    _posthog.reset()
  },

  /** Get a feature flag value. Returns undefined if PostHog not ready. */
  getFeatureFlag(flag: string): boolean | string | undefined {
    if (!_posthog) return undefined
    const value = _posthog.getFeatureFlag(flag)
    return value === null ? undefined : value
  },

  /** Check if a boolean feature flag is enabled. */
  isFeatureEnabled(flag: string): boolean {
    if (!_posthog) return false
    return _posthog.isFeatureEnabled(flag) ?? false
  },

  /** Whether PostHog is loaded and ready. */
  isReady(): boolean {
    return _initialized && _posthog !== null
  },

  /** Shut down and clear identity (called when consent is revoked). */
  shutdown(): void {
    if (!_posthog) return
    _posthog.reset()
    _posthog.opt_out_capturing()
    _posthog = null
    _initialized = false
  },
}

export default posthogService
